import React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import { LoadingOverlay } from './Conmponents/LoadingOverlay';
import Pagination from './Paginaion';

export interface TableColumn<T> {
  key: keyof T | string;
  label: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (row: T) => React.ReactNode;
  disableSort?: boolean;
  sortValue?: (row: T) => number | string;
}

export interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

export interface DataTableProps<T> {
  isLoading?: boolean;
  columns?: TableColumn<T>[];
  data?: T[];
  rows?: T[];
  rowsPerPage: number;
  onClickRow?: (row: T) => void;
  onRowClick?: (row: T) => void;
  sortConfig?: SortConfig[];
  onSortChange?: (key: string, direction: 'asc' | 'desc') => void;
  currentPage: number;
  totalRecords: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  hasCheckBox?: boolean;
  hidePagination?: boolean;
  onSelectAll?: () => void;
  onSelectRow?: (rowIndex: number, row: T) => void;
  isRowSelected?: (rowIndex: number, row: T) => boolean;
  isAllSelected?: boolean;
  isIndeterminate?: boolean;
  hasHidePagination?: boolean;
  minTableHeight?: string;
  emptyTitle?: string;
  emptySubtitle?: string;
  emptyContent?: React.ReactNode;
}

export const DataTable = <T,>({
  columns = [],
  data,
  rows,
  rowsPerPage = 5,
  onClickRow,
  onRowClick,
  isLoading,
  sortConfig = [],
  onSortChange,
  currentPage,
  totalPages,
  totalRecords,
  onPageChange,
  hasCheckBox,
  hidePagination,
  onSelectAll,
  onSelectRow,
  isRowSelected,
  isAllSelected = false,
  isIndeterminate = false,
  hasHidePagination,
  minTableHeight = 'auto',
  emptyTitle = 'No Data Available',
  emptySubtitle = 'There are no records to display in this category at the moment.',
  emptyContent,
}: DataTableProps<T>) => {
  const tableData = data ?? rows ?? [];
  const handleRowClick = onClickRow ?? onRowClick;
  const handleSort = (key: string) => {
    if (!onSortChange || columns.find((col) => col.key === key)?.disableSort) {
      return;
    }

    const existingSortIndex = sortConfig.findIndex((sort) => sort.key === key);
    const newSortConfig = [...sortConfig];

    if (existingSortIndex !== -1) {
      const currentSort = newSortConfig[existingSortIndex];
      const newDirection = currentSort.direction === 'asc' ? 'desc' : 'asc';
      newSortConfig[existingSortIndex] = { key, direction: newDirection };
    } else {
      newSortConfig.push({ key, direction: 'asc' });
    }

    const { direction } =
      newSortConfig[
        existingSortIndex !== -1 ? existingSortIndex : newSortConfig.length - 1
      ];

    onSortChange(key, direction);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        minWidth: 0,
        borderRadius: '6px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <TableContainer
        sx={{
          flex: '1 1 auto',
          minHeight: minTableHeight && minTableHeight !== 'auto' ? minTableHeight : undefined,
          position: 'relative',
        }}
      >
        <Table stickyHeader sx={{ minWidth: 520 }}>
          <TableHead>
            <TableRow>
              {hasCheckBox && (
                <TableCell
                  padding="checkbox"
                  sx={{
                    bgcolor: 'rgba(248, 250, 252, 0.95)',
                    backdropFilter: 'blur(8px)',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    width: 48,
                    pl: 2,
                  }}
                >
                  <Checkbox
                    size="small"
                    color="primary"
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={onSelectAll}
                  />
                </TableCell>
              )}

              {columns.map((col, index) => {
                const columnSort = sortConfig?.find(
                  (s) => s.key === String(col.key),
                );
                const isAsc = columnSort?.direction === 'asc';

                return (
                  <TableCell
                    key={index}
                    align={col.align || 'left'}
                    sx={{
                      bgcolor: 'rgba(248, 250, 252, 0.95)',
                      backdropFilter: 'blur(8px)',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      py: 1.5,
                      px: 2,
                      width: col.width || 'auto',
                      minWidth: col.width || 'auto',
                      fontSize: '0.6875rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: 'text.secondary',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {!col.disableSort ? (
                      <TableSortLabel
                        active={Boolean(columnSort)}
                        direction={isAsc ? 'asc' : 'desc'}
                        onClick={() => handleSort(String(col.key))}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            fontSize: '16px !important',
                          },
                        }}
                      >
                        {col.label.length > 24 ? (
                          <Tooltip title={col.label} arrow>
                            <span>{col.label}</span>
                          </Tooltip>
                        ) : (
                          col.label
                        )}
                      </TableSortLabel>
                    ) : col.label.length > 24 ? (
                      <Tooltip title={col.label} arrow>
                        <span>{col.label}</span>
                      </Tooltip>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading && tableData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (hasCheckBox ? 1 : 0)}
                  sx={{ py: 8, textAlign: 'center' }}
                >
                  <LoadingOverlay />
                </TableCell>
              </TableRow>
            ) : tableData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (hasCheckBox ? 1 : 0)}
                  sx={{ py: emptyContent ? 0 : 8, textAlign: 'center', border: 0 }}
                >
                  {emptyContent ? (
                    emptyContent
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 4,
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: '50%',
                          bgcolor: 'action.hover',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'text.disabled',
                          mb: 1,
                        }}
                      >
                        <InboxRoundedIcon sx={{ fontSize: 32 }} />
                      </Box>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'text.primary' }}>
                        {emptyTitle}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', maxWidth: 300 }}>
                        {emptySubtitle}
                      </Typography>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              tableData.map((row, rowIndex) => {
                const selected = Boolean(isRowSelected?.(rowIndex, row));
                return (
                  <TableRow
                    key={(row as { id?: string | number })?.id ?? rowIndex}
                    hover
                    selected={selected}
                    onClick={() => handleRowClick?.(row)}
                    sx={{
                      cursor: handleRowClick ? 'pointer' : 'default',
                      transition: 'background-color 0.15s ease',
                      '&.Mui-selected': {
                        bgcolor: 'rgba(16, 185, 129, 0.06)',
                        '&:hover': {
                          bgcolor: 'rgba(16, 185, 129, 0.1)',
                        },
                      },
                    }}
                  >
                    {hasCheckBox && (
                      <TableCell padding="checkbox" sx={{ pl: 2 }}>
                        <Checkbox
                          size="small"
                          color="primary"
                          checked={selected}
                          onChange={(e) => {
                            e.stopPropagation();
                            onSelectRow?.(rowIndex, row);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                    )}

                    {columns.map((column, colIndex) => (
                      <TableCell
                        key={colIndex}
                        align={column.align || 'left'}
                        sx={{
                          py: 1.75,
                          px: 2,
                          fontSize: '0.8125rem',
                          color: 'text.primary',
                          whiteSpace: 'nowrap',
                          width: column.width || 'auto',
                        }}
                      >
                        {column.render
                          ? column.render(row)
                          : (row[column.key as keyof T] as React.ReactNode)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {isLoading && tableData.length > 0 && <LoadingOverlay />}
      </TableContainer>

      {!hidePagination && !isLoading && tableData.length > 0 && (
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
          <Pagination
            totalRecords={totalRecords}
            currentPage={currentPage}
            handlePageChange={onPageChange}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            hasHidePagination={hasHidePagination}
          />
        </Box>
      )}
    </Paper>
  );
};
